"use client";

import React, { memo, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_OPEN_ATTRIBUTE } from "./disclosure.shared";
import { useLineupSearchSelection } from "./lineup-search.client";
import { SearchResults } from "./search-results";
import { SignatureGrid, type SignatureGridProps } from "./signature-grid";
import {
  type BuildSignatureSectionsInput,
  type SignatureSearchResults,
  type SignatureSearchStatus,
  buildSignatureSearchResults,
  buildSignatureSections,
  createSignatureRowPool,
  signatureSearchAnnouncement,
  signatureSearchTerms
} from "./signature-grid.shared";
import styles from "./signature-grid.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/* Before paint, so a reopened results bar never shows one closed frame; useEffect on the server,
   where useLayoutEffect warns. */
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * The Signature packages grid, browsing or searching — GQ1-GQ7, AX4. the only client boundary this
 * view adds, and it holds no state of its own.
 */

export interface SearchableSignatureGridProps
  extends Omit<SignatureGridProps, "sections">,
    Omit<BuildSignatureSectionsInput, "columns"> {
  /**
   * GQ6 — how the search results arrived. `"partial"` draws its message and the results; `"error"`
   * draws its message instead of them. Ignored while browsing, because GQ6 is about a search
   * failing to load.
   */
  status?: SignatureSearchStatus;
  /** GQ3's heading wording. */
  search_heading_content?: string;
  /** GQ6's partial wording. */
  search_partial_message_content?: string;
  /** GQ6's total-failure wording. */
  search_error_message_content?: string;
}

/* Memoised so the urgent render of a chip change — which still holds the
   previous deferred selection — bails out of the ~650 rows instead of
   re-rendering them with identical props. */
const MemoSignatureGrid = memo(SignatureGrid);
const MemoSearchResults = memo(SearchResults);

const EMPTY_SEARCH_RESULTS: SignatureSearchResults = {
  subscriptionRows: [],
  rows: [],
  myFreeRows: [],
  myFreeLabel: "",
  memberships: [],
  total: 0
};

function SearchableSignatureGridImpl({
  sort,
  columns,
  channels,
  categories,
  subscriptionRows,
  myFreeLabel,
  subscriptionsHeading,
  includedHeading,
  addonHeading,
  localHeading,
  rsnHeading,
  platform,
  status = "ready",
  search_heading_content: searchHeadingContent,
  search_partial_message_content: partialMessageContent,
  search_error_message_content: searchErrorMessageContent,
  ...grid
}: SearchableSignatureGridProps) {
  /* The chip and Reset paint first; swapping grid and results follows and can
     be interrupted by the next change. */
  const { channelIds, subscriptionIds, selectedIds, matchedIds, selectedLabels, isActive } =
    useDeferredValue(useLineupSearchSelection());
  const browseRef = useRef<HTMLDivElement>(null);

  /* The browse grid mounts the first time it is shown and is then kept,
     `hidden` under a search, so clearing one does not rebuild every row. */
  const [browseMounted, setBrowseMounted] = useState(!isActive);
  if (!isActive && !browseMounted) {
    setBrowseMounted(true);
  }

  /* GQ7 — "clearing the search returns the grid with the default sections
     open". Reset as the search opens, while the grid is hidden, so the
     `toggle` events find no box and `SectionScrollKeeper` does not scroll. */
  useEffect(() => {
    if (!isActive) {
      return;
    }
    browseRef.current?.querySelectorAll("details").forEach(details => {
      details.open = details.hasAttribute(DEFAULT_OPEN_ATTRIBUTE);
    });
  }, [isActive]);

  /* Memoised on purpose, and this is one of the three places the guidelines name. */
  const search = useMemo(
    () => {
      if (!isActive) {
        return undefined;
      }
      /* GQ6, as production's grid does: a merge that throws shows the
         total-failure message rather than taking the lineup down with it. */
      try {
        return {
          results: buildSignatureSearchResults({
            categories,
            channelIds,
            channels,
            columns,
            myFreeLabel,
            platform,
            sort,
            subscriptionIds,
            subscriptionRows
          }),
          failed: false
        };
      } catch (error) {
        console.error("[SearchableSignatureGrid] search results failed to build", error);
        return {
          results: EMPTY_SEARCH_RESULTS,
          failed: true
        };
      }
    },
    [
      channelIds,
      channels,
      columns,
      isActive,
      myFreeLabel,
      platform,
      subscriptionIds,
      subscriptionRows
    ]
  );
  const results = search?.results;
  const searchStatus: SignatureSearchStatus = search?.failed ? "error" : status;

  /* The results bar collapses (comp `16562:25174`), but a NEW search must never land behind a bar
     the reader closed for the previous one: AX4 would announce rows nobody can see. */
  const resultsRef = useRef<HTMLDivElement>(null);
  useIsomorphicLayoutEffect(() => {
    resultsRef.current?.querySelectorAll(`details[${DEFAULT_OPEN_ATTRIBUTE}]`).forEach(details => {
      (details as HTMLDetailsElement).open = true;
    });
  }, [search]);

  /* One row object per channel across sorts, so a re-sort hands the memoised
     `ChannelRow` the same `row` and React reorders instead of re-rendering. */
  const rowPool = useMemo(() => createSignatureRowPool(columns), [columns]);

  /* Independent of the search, so the kept grid is not regrouped by one. */
  const sections = useMemo(
    () => (browseMounted ?
      buildSignatureSections({
        addonHeading,
        categories,
        channels,
        columns,
        includedHeading,
        localHeading,
        myFreeLabel,
        platform,
        rowPool,
        rsnHeading,
        sort,
        subscriptionRows,
        subscriptionsHeading
      }) :
      []),
    [
      addonHeading,
      browseMounted,
      categories,
      channels,
      columns,
      includedHeading,
      localHeading,
      myFreeLabel,
      platform,
      rowPool,
      rsnHeading,
      sort,
      subscriptionRows,
      subscriptionsHeading
    ]
  );

  /* GS7 — a sort keeps the reader's open/closed sections; resetting them
     collapsed a section the reader opened and jumped the page (CLS 0.61).
     Defaults return on first load, a view switch and a cleared search. */

  /* AX4 — the wording the reader hears. Built from the SAME resolved copy the
     results draw, so the announcement cannot quote a message the grid is not
     showing. */
  const announcement = signatureSearchAnnouncement({
    errorMessage: (searchErrorMessageContent ?? "").trim(),
    isActive,
    noMatchMessage: (grid.empty_message_content ?? "").trim(),
    partialMessage: (partialMessageContent ?? "").trim(),
    status: searchStatus,
    /* `selectedLabels` is what lets a carried chip be named after a ZIP change: the new market has
       no row to resolve it against, and the provider is holding the wording the chip is still
       showing. */
    terms: signatureSearchTerms({
      channels,
      matchedIds,
      selectedIds,
      selectedLabels,
      subscriptionRows
    }),
    /* The real count, uncoerced. */
    total: results?.total ?? 0
  });

  return (
    <>
      {/* AX4. always rendered, and never inside the keyed subtree. */}
      <p aria-live="polite" className="dds-sr-only" role="status">
        {announcement}
      </p>

      {isActive && results !== undefined ?
        <div className={styles.searchResults} ref={resultsRef}>
          <MemoSearchResults
            className={grid.className}
            columns={columns}
            empty_message_content={grid.empty_message_content}
            error_message_content={searchErrorMessageContent}
            /* AX5 — one authored level governs both branches. */
            headingLevel={grid.sectionHeadingLevel}
            legend_addon_content={grid.legend_addon_content}
            legend_included_content={grid.legend_included_content}
            legend_promo_content={grid.legend_promo_content}
            myfree_cta_aria_label={grid.myfree_cta_aria_label}
            myfree_cta_label={grid.myfree_cta_label}
            myfree_cta_url={grid.myfree_cta_url}
            myfree_logo_url={grid.myfree_logo_url}
            partial_message_content={partialMessageContent}
            results={results}
            rowCopy={grid.rowCopy}
            rsn_legal_default_content={grid.rsn_legal_default_content}
            search_heading_content={searchHeadingContent}
            /* AX6 — `=== true`, not a truthiness test, for the reason `signature-grid.tsx` spells
               out: Storyblok publishes an unset switch as the string "false", which is truthy. */
            show_channel_logo_tooltip={grid.show_channel_logo_tooltip === true}
            status={searchStatus}
            tierLegalNotes={grid.tierLegalNotes}
          />
        </div> :
        null}

      {browseMounted ?
        <div hidden={isActive} ref={browseRef}>
          <MemoSignatureGrid
            {...grid}
            columns={columns}
            /* No `key`: a sort reorders the rows in place. GS7's defaults are
               the layout effect above; GQ7's are the search effect. */
            sections={sections}
          />
        </div> :
        null}
    </>
  );
}
export const SearchableSignatureGrid = withSafeRender(SearchableSignatureGridImpl, "SearchableSignatureGrid");

export default SearchableSignatureGrid;
